// Error type for /transfer-api/v1 responses and the mapping from HTTP status + error.code to UI meaning.
// The mapping is an ASSUMPTION recorded in CONTRACT-QUESTIONS.md (Q1); keep it in this one place.

export class TransferApiError extends Error {
  constructor(status, code, message, extra = {}) {
    super(message || code || `HTTP ${status}`);
    this.name = 'TransferApiError';
    this.status = status;
    this.code = code || null;
    this.retryAfterMs = extra.retryAfterMs ?? null;
  }
}

/** Engine-level failure with a stable code and a user-safe message. */
export class TransferFailure extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = 'TransferFailure';
    this.code = code;
    if (cause) this.cause = cause;
  }
}

const norm = (c) => String(c || '').toLowerCase().replace(/-/g, '_');

/**
 * @returns {'password-required'|'password-invalid'|'denied'|'missing'|'expired'|'rate-limited'|'too-large'|'range'|'conflict'|'bad-request'|'unauthorized'|'server'|'network'}
 */
export function classify(err) {
  if (!(err instanceof TransferApiError)) return 'network';
  const c = norm(err.code), s = err.status;
  if (c === 'password_required') return 'password-required';
  if (c === 'invalid_password' || c === 'password_invalid' || c === 'wrong_password') return 'password-invalid';
  if (s === 410 || c === 'expired' || c === 'session_expired' || c === 'token_expired' || c === 'link_expired') return 'expired';
  if (s === 404) return 'missing';
  if (s === 429) return 'rate-limited';
  if (s === 413) return 'too-large';
  if (s === 416) return 'range';
  if (s === 409) return 'conflict';
  if (s === 401) return 'unauthorized';
  if (s === 403) return 'denied';
  if (s >= 500) return 'server';
  return 'bad-request';
}

/** Statuses worth retrying with back-off. */
export function isTransient(kind) {
  return kind === 'network' || kind === 'server' || kind === 'rate-limited';
}

export const MESSAGES = {
  'piece-integrity': 'A piece kept failing verification. Nothing unverified was kept. You can try again.',
  'file-integrity': 'The finished file did not match its fingerprint, so it was discarded. You can try again.',
  'version-changed': 'The file changed on the server. Start the download again to get the new version.',
  'bad-manifest': 'The server described this file in a way we could not trust, so nothing was downloaded.',
  'network': 'The connection keeps failing. Verified pieces are kept. You can try again.',
  'server': 'The server is not answering right now. Verified pieces are kept. You can try again.',
  'denied': 'You do not have access to this file.',
  'missing': 'This file is no longer available.',
  'storage': 'This device does not have enough free storage for this file.',
  'storage-write': 'Your browser refused to store a piece. Free up space and try again.',
  'unsupported': 'This browser cannot store large downloads. Use a current Chrome, Edge, Firefox or Safari.',
  'save-failed': 'Your browser did not confirm the write. Verified pieces are kept. Try saving again.',
  'save-dismissed': 'Save was cancelled. The file is still ready.',
  'save-handed-off': 'Your browser is saving the file. Check your downloads.',
  'upload-failed': 'The upload could not finish. Nothing was published. You can try again.',
  'commit-mismatch': 'The server stored different bytes than this device sent. Nothing was published.',
  'too-large': 'This file is larger than the server allows.',
  'native-download': 'Your browser is downloading this file directly from the server. Check your downloads.',
  'native-unavailable': 'The direct download link could not be prepared. Try again.',
  'native-storage': 'This browser cannot keep this file locally, so it is downloading directly from the server. Check your downloads.',
  'native-fallback': 'The download continues directly from the server. Check your downloads.',
  'native-on-retry': 'Try again downloads it directly from the server.',
  'owning-unavailable': 'This download can no longer use the faster transfer.',
  'operator-required': 'Uploads in this demo need the operator credential.',
  'operator-rejected': 'The operator credential was not accepted. Nothing was uploaded.',
};
